import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Factory,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  ClipboardList,
  Star,
  Target,
  BarChart3,
  Building2,
  Utensils,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const IFS_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-violet-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-fuchsia-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <Utensils size={18} className="text-violet-400" />
            <span>International Food Safety Standards</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-400">
            IFS Certification
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            International Featured Standards - Global Food Safety and Quality
            Management for International Markets
          </p>
        </motion.div>
      </section>

      {/* What is IFS */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is IFS Certification?
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p>
              IFS certification confirms that an organization has implemented
              and follows internationally recognized food safety management
              standards to ensure the safety and quality of food products
              throughout the food supply chain.
            </p>
            <p>
              The International Featured Standards (IFS) is an internationally
              recognized standard for food safety and quality management. IFS
              certification is crucial for gaining market access in regions
              where food safety regulations are stringent, especially in Europe
              and global supply chains recognized by retailers and food
              manufacturers worldwide.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Key Features of IFS Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Comprehensive Food Safety Management",
                desc: "Ensures food products are safe by meeting rigorous food safety standards.",
                icon: ShieldCheck,
              },
              {
                title: "Quality Assurance",
                desc: "Verifies products consistently meet quality standards across production.",
                icon: CheckCircle,
              },
              {
                title: "Regulatory Compliance",
                desc: "Ensures compliance with food safety laws and regulations.",
                icon: FileCheck,
              },
              {
                title: "Traceability & Transparency",
                desc: "Provides robust mechanisms for tracking products throughout supply chain.",
                icon: Globe,
              },
              {
                title: "Supply Chain Management",
                desc: "Ensures suppliers and contractors meet food safety and quality standards.",
                icon: Factory,
              },
              {
                title: "Risk-Based Approach",
                desc: "Applies HACCP principles to manage food safety risks effectively.",
                icon: Target,
              },
            ].map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <IconComponent className="w-10 h-10 text-violet-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* Food Safety & Quality Management */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Food Safety and Quality Management for IFS
          </h2>
          <div className="bg-gradient-to-r from-violet-50 to-fuchsia-50 p-8 rounded-lg">
            <p className="text-lg text-gray-700 mb-6">
              Food Safety and Quality Management is a structured approach that
              ensures:
            </p>
            <ul className="space-y-3">
              {[
                "Hazard Identification & Risk Assessment throughout the supply chain",
                "HACCP Principles applied to manage food safety risks effectively",
                "Control of Critical Points ensuring product safety and quality",
                "Supply Chain Traceability from production to distribution and retail",
                "Employee Training ensuring staff compliance with food safety practices",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </section>

      {/* Importance */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Importance of IFS Certification for Businesses
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Market Access widely required by retailers especially in Europe",
              "Regulatory Compliance ensuring adherence to food safety standards",
              "Consumer Confidence demonstrating commitment to food safety",
              "Improved Product Quality establishing consistent quality standards",
              "Supply Chain Assurance strengthening relationships with suppliers",
              "Global Recognition for international business opportunities",
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4"
              >
                <Star className="w-6 h-6 text-violet-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Significance */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Significance of IFS Certification
          </h2>
          <p className="text-lg text-gray-700 mb-6">
            IFS certification helps organizations:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Demonstrate Commitment to Food Safety and quality standards",
              "Meet Global Market Requirements for strict regulations",
              "Ensure Product Safety reducing contamination risks",
              "Gain Competitive Advantage through recognized certification",
              "Strengthen Consumer Trust with highest safety standards",
              "Access International Retailers and supply chains",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-violet-100 to-fuchsia-100 p-6 rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-violet-600 mb-3" />
                <p className="text-gray-800 font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Advantages */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Advantages of IFS Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Increased Market Access to international markets globally",
              "Improved Food Safety and quality consistency",
              "Regulatory Compliance with food safety standards",
              "Operational Efficiency streamlining processes",
              "Enhanced Reputation demonstrating quality commitment",
              "Supply Chain Transparency providing traceability",
              "Retailer Recognition meeting major retailer requirements",
              "Consumer Confidence through verified safety standards",
            ].map((advantage, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 p-4 bg-white rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Industries That Require IFS Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Food Manufacturers & Processors",
              "Food Packaging Companies",
              "Food Retailers",
              "Logistics & Distribution",
              "Restaurants & Food Service",
              "Food Storage & Handling",
            ].map((industry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-violet-50 to-fuchsia-50 rounded-lg"
              >
                <Factory className="w-5 h-5 text-violet-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{industry}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How to Get Certification */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            How to Get IFS Certification
          </h2>
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: "Gap Analysis",
                desc: "Assess current practices against IFS standards",
              },
              {
                step: 2,
                title: "Documentation Development",
                desc: "Develop necessary documentation for food safety management",
              },
              {
                step: 3,
                title: "Employee Training",
                desc: "Train all staff in food safety and regulatory compliance",
              },
              {
                step: 4,
                title: "Implementation",
                desc: "Implement food safety and quality control across production",
              },
              {
                step: 5,
                title: "Internal Audits",
                desc: "Conduct internal audits evaluating system effectiveness",
              },
              {
                step: 6,
                title: "Certification Audit",
                desc: "Undergo audit by accredited certification body",
              },
              {
                step: 7,
                title: "Ongoing Monitoring",
                desc: "Continuously monitor and improve food safety practices",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-r from-violet-50 via-purple-50 to-fuchsia-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900 text-center">
            Why Choose MBA Solutions for IFS Certification?
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              MBA Solutions Pvt. Ltd. provides expert IFS consultancy services,
              assisting food businesses in implementing and maintaining an
              effective food safety and quality management system.
            </p>
            <ul className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "IFS FSMS and HACCP Plan Development",
                "Supply Chain Management and Traceability",
                "Employee Training on Food Safety",
                "Internal Audits and Pre-Certification",
                "Certification Body Liaison",
                "Audit Support and Coordination",
              ].map((service, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{service}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 mb-6">
              We help food businesses achieve safe, compliant, and high-quality
              food production through expert IFS implementation.
            </p>
            <div className="border-t pt-6 mt-6">
              <p className="text-gray-800 font-semibold mb-4">Get in Touch:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-violet-600" />
                  <span className="text-gray-700">
                    <strong>Email:</strong> atif@mbasolutions.co
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-violet-600" />
                  <span className="text-gray-700">
                    <strong>Website:</strong> www.mbasolutions.co
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </>
  );
};

export default IFS_Details;
